<script setup lang="ts">
import { ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import { PLAN_UNITS } from "@/utils/plan-analysis/plan-units";
import type { PlanUnit } from "@/utils/plan-analysis/types";

/**
 * Validation du lancement de l'analyse IA — affichée à CHAQUE lancement.
 * C'est le seul endroit où se règle l'unité du plan : présélectionnée quand
 * elle est déjà connue, toujours modifiable, obligatoire pour confirmer
 * (l'IA rédige les mesures avec elle). Les BET travaillent en mètres ou
 * millimètres, parfois en centimètres : trois choix, pas plus.
 */

const props = defineProps<{
  modelValue: boolean;
  /** Unité présélectionnée : celle du plan, sinon celle que déclare le DWG. */
  initialUnit?: PlanUnit | null;
  /** Une analyse existe déjà : la relance remplace les groupes en attente. */
  relaunch?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", visible: boolean): void;
  (e: "confirm", unit: PlanUnit): void;
}>();

const { t } = useTranslation();

const selected = ref<PlanUnit | null>(props.initialUnit ?? null);

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) selected.value = props.initialUnit ?? null;
  },
);

const confirm = (): void => {
  if (!selected.value) return;
  emit("update:modelValue", false);
  emit("confirm", selected.value);
};
</script>

<template>
  <ConfirmationModal
    class="canvas-dialog"
    :model-value="modelValue"
    :title="
      t(
        relaunch
          ? 'canvas:analysis.launch.relaunchTitle'
          : 'canvas:analysis.launch.title',
      )
    "
    :message="
      t(
        relaunch
          ? 'canvas:analysis.launch.relaunchMessage'
          : 'canvas:analysis.launch.message',
      )
    "
    :confirm-label="
      t(
        relaunch
          ? 'canvas:analysis.launch.relaunchConfirm'
          : 'canvas:analysis.launch.confirm',
      )
    "
    confirm-variant="ia"
    :confirm-disabled="!selected"
    @update:model-value="emit('update:modelValue', $event)"
    @confirm="confirm"
  >
    <p class="unit-question mb-2">
      {{ t("canvas:analysis.launch.unitQuestion") }}
    </p>
    <div class="d-flex gap-2">
      <button
        v-for="unit in PLAN_UNITS"
        :key="unit"
        type="button"
        class="unit-option flex-fill d-flex flex-column align-items-center"
        :class="{ active: selected === unit }"
        @click="selected = unit"
      >
        <span class="unit-symbol">{{ unit }}</span>
        <span class="unit-name">{{ t(`canvas:analysis.unit.${unit}`) }}</span>
      </button>
    </div>
  </ConfirmationModal>
</template>

<style lang="scss" scoped>
.unit-question {
  font-size: 0.9rem;
}

.unit-option {
  border: 1px solid var(--el-border-color);
  border-radius: 10px;
  background: transparent;
  color: inherit;
  padding: 10px 6px;
  gap: 2px;
  cursor: pointer;

  &:hover {
    border-color: var(--dr-report);
  }
  &.active {
    border-color: var(--dr-report);
    background: color-mix(in srgb, var(--dr-report) 18%, transparent);
  }
}

.unit-symbol {
  font-size: 1.2rem;
  font-weight: 700;
}

.unit-name {
  font-size: 0.72rem;
  opacity: 0.7;
}
</style>
